import { Extension, Mark } from '@tiptap/core';
import i18n from '@/shared/i18n';

/**
 * Adds a `data-diff-anchor` attribute to block-level nodes so the rendered
 * diff carries stable, queryable anchors (one per changed block). A sidebar
 * change-navigator scrolls the document via
 * `querySelector('[data-diff-anchor="N"]')` → `scrollIntoView`. Registered
 * only on the read-only diff editor; the index values are assigned by
 * diffToTipTapContent and line up with getDiffChangeSummaries.
 */
export const DiffAnchor = Extension.create({
  name: 'diffAnchor',

  addGlobalAttributes() {
    return [
      {
        types: ['heading', 'paragraph', 'listItem', 'table', 'tableRow'],
        attributes: {
          dataDiffAnchor: {
            default: null,
            parseHTML: element => element.getAttribute('data-diff-anchor'),
            renderHTML: attributes =>
              attributes.dataDiffAnchor == null
                ? {}
                : { 'data-diff-anchor': attributes.dataDiffAnchor },
          },
        },
      },
    ];
  },
});

/**
 * Custom TipTap mark for text that was added in a diff.
 * Renders as an <ins> element with green styling.
 */
export const DiffAdded = Mark.create({
  name: 'diffAdded',

  parseHTML() {
    return [{ tag: 'ins' }];
  },

  renderHTML() {
    return [
      'ins',
      {
        class: 'diff-added',
        'aria-label': i18n.t('common.diff.added'),
      },
      0,
    ];
  },
});

/**
 * Custom TipTap mark for text that was removed in a diff.
 * Renders as a <del> element with red styling and strikethrough.
 */
export const DiffDeleted = Mark.create({
  name: 'diffDeleted',

  parseHTML() {
    return [{ tag: 'del' }];
  },

  renderHTML() {
    return [
      'del',
      {
        class: 'diff-deleted',
        'aria-label': i18n.t('common.diff.deleted'),
      },
      0,
    ];
  },
});
